import api from '@/lib/api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Returns { ok: boolean, reason?: 'unsupported'|'denied'|'error' }
export async function enablePush() {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: 'denied' };

    const reg = await navigator.serviceWorker.ready;
    const { data } = await api.get('/push/vapid-public-key');
    if (!data?.public_key) return { ok: false, reason: 'error' };

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }
    await api.post('/push/subscribe', { subscription: sub.toJSON() });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'error' };
  }
}

export async function disablePush() {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api.post('/push/unsubscribe', { subscription: sub.toJSON() }).catch(() => {});
      await sub.unsubscribe().catch(() => {});
    }
  } catch (e) { console.debug('disablePush cleanup skipped:', e?.message || e); }
}

export async function isSubscribedOnThisDevice() {
  if (!pushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    return !!(await reg.pushManager.getSubscription());
  } catch (e) { return false; }
}

// Silent re-sync used on app load: only subscribes if the user already
// granted permission on THIS device. Ensures each device registers its own
// subscription even when notifications were enabled on another device.
export async function syncPush() {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };
  if (Notification.permission !== 'granted') return { ok: false, reason: 'no-permission' };
  return enablePush();
}
